import { forwardRef, useCallback, useRef } from 'react'
import type { InputHTMLAttributes } from 'react'
import clsx from 'clsx'
import { Search, XClear } from '@/assets/icons'
import { IconButton } from '../icon-button/IconButton'

/**
 * Props for the SearchField component.
 */
export interface SearchFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'size' | 'type' | 'value' | 'defaultValue'> {
  /**
   * Current value of the search input.
   */
  value: string

  /**
   * Called on every keystroke with the current input value.
   */
  onChange: (value: string) => void

  /**
   * Placeholder text shown when the input is empty.
   * @default 'Search'
   */
  placeholder?: string

  /**
   * Whether the search field is disabled.
   * @default false
   */
  disabled?: boolean

  /**
   * Additional class names applied to the outer wrapper element.
   */
  className?: string
}

/**
 * SearchField Component
 *
 * A reusable search input with a leading search icon and a trailing clear button (×).
 *
 * The component is full-width by default —  adapting to its container. This is intentional
 * since search fields are typically used as bars at the top of pages, in headers, or spanning sidebars.
 *
 * @example
 * Real-time filtering — updates on every keystroke
 *
 * const [query, setQuery] = useState('')
 * <SearchField value={query} onChange={setQuery} placeholder="Search matters..." />
 *
 * @example
 * Explicit search — only searches when user presses Enter
 *
 * Note: We don't currently have a use case for explicit search in the app,
 * so we're not adding an onSearch prop to avoid over-engineering.
 * This pattern can be achieved with the native onKeyDown prop shown below.
 * If explicit search becomes a common pattern, we can add dedicated API support.
 *
 * const [query, setQuery] = useState('')
 * const handleSearch = () => fetchResults(query)
 * <SearchField
 *   value={query}
 *   onChange={setQuery}
 *   onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
 *   placeholder="Search..."
 * />
 *
 */
const SearchField = forwardRef<HTMLInputElement, SearchFieldProps>(function SearchField(
  { value, onChange, placeholder = 'Search', disabled = false, className, ...props },
  forwardedRef
) {
  const internalRef = useRef<HTMLInputElement | null>(null)

  const showClearButton = value.length > 0

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value)
  }

  const handleClear = () => {
    onChange('')

    // Return focus to input so the user can keep typing
    internalRef.current?.focus()
  }

  // Connect the input element to both our internal ref (for clearing/focusing)
  // and any ref the parent passed in (so they can also control the input)
  const setRef = useCallback(
    (node: HTMLInputElement | null) => {
      internalRef.current = node
      if (typeof forwardedRef === 'function') {
        forwardedRef(node)
      } else if (forwardedRef) {
        const refObject = forwardedRef as React.MutableRefObject<HTMLInputElement | null>
        refObject.current = node
      }
    },
    [forwardedRef]
  )

  return (
    <div
      data-ds-component="SearchField"
      className={clsx(
        'flex items-center gap-2 pl-3 pr-0.5 h-[44px] w-full',
        'bg-white rounded border border-brand-neutral-400',
        // Focus ring — use focus-within so the border updates when the input inside is focused
        'focus-within:border-brand-400',
        'transition-colors duration-200',
        // Disabled
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
    >
      <div className="p-0.5">
        <Search className="h-5 w-5 shrink-0 text-brand-neutral-600" aria-hidden />
      </div>

      <input
        {...props}
        ref={setRef}
        type="search"
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        className={clsx(
          'flex-1 min-w-0 bg-transparent',
          // Reset @tailwindcss/forms styles so the border lives on the wrapper only
          'border-0 p-0 m-0 ring-0 shadow-none',
          'focus:outline-none focus:ring-0 focus:shadow-none',
          'text-base text-brand-neutral-950 placeholder:text-brand-neutral-600',
          'disabled:cursor-not-allowed',
          // Hide native search clear button since we have our own custom implementation
          '[&::-webkit-search-cancel-button]:appearance-none'
        )}
      />

      {showClearButton && !disabled && (
        <IconButton icon={<XClear />} size="lg" variant="plain" color="neutral" aria-label="Clear search" onClick={handleClear} />
      )}
    </div>
  )
})

export { SearchField }
